import React, { FC } from 'react';
import { Tile } from 'components/commercetools-ui/organisms/header/types';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import useClassNames from 'helpers/hooks/useClassNames';
import HeaderDropdownTile from './header-menu-tile';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import Button from 'components/commercetools-ui/atoms/button';
import { PlusIcon } from '@heroicons/react/24/solid';

export interface Props {
  show?: boolean;
  links: any[];
  tileContent?: Tile;
  onClick?: () => void;
}

/* ---------------------------------------------
   CONFIG: UI class mappings per title or layout
----------------------------------------------*/
const COLUMN_CONFIG: Record<string, string> = {
  'Planning tools': 'col-span-6',
  'Collection': 'col-span-4',
  'Spares': 'col-span-2',
  'Renovation guides': 'col-span-6',
  'About Us': 'col-span-4',
  'Support': 'col-span-4',
  'Our Other Brands': 'col-span-4',
  'Product Range': 'col-span-6',
  'Kitchen & Laundry': 'col-span-10 xl:col-span-8',
};

const GRID_CONFIG: Record<string, string> = {
  'Planning tools': 'grid-cols-1',
  'Collection': 'grid-cols-2 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30 mr-[25px]',
  'Spares': 'grid-cols-1',
  'Renovation guides': 'grid-cols-3 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30',
  'About Us': 'grid-cols-2 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30 mr-[25px]',
  'Support': 'grid-cols-2 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30 mr-[25px]',
  'Our Other Brands': 'grid-cols-2 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30',
  'Product Range': 'grid-cols-3 gap-x-24 gap-y-6 2xl:gap-x-6 2xl:gap-y-30',
  'Kitchen & Laundry': 'grid-cols-3 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30',
};

const IMAGE_STYLE_CONFIG = {
  default: 'font-normal text-black md:text-12 2xl:text-14 [&>img]:h-[4.25rem] [&>img]:w-[7.5rem] xl:[&>img]:h-[6.25rem] xl:[&>img]:w-[9.5rem] 3xl:[&>img]:h-[8.25rem] 3xl:[&>img]:w-[12.5rem]',
  large: 'font-normal text-[#333] md:text-14 [&>img]:h-[10.5rem] [&>img]:w-[8.25rem] xl:[&>img]:h-[13.25rem] xl:[&>img]:w-[12rem] 3xl:[&>img]:h-[17.5rem] 3xl:[&>img]:w-[16rem]',
  small: 'font-normal text-black md:text-12 2xl:text-14 [&>img]:h-[4.25rem] [&>img]:w-[7.5rem] xl:[&>img]:h-[6.25rem] xl:[&>img]:w-[9.5rem] 3xl:[&>img]:h-[8.25rem] 3xl:[&>img]:w-[12.5rem]',
};

const MenuDropdown: FC<Props> = ({ show, links, tileContent }) => {
  const wrapperClassNames = useClassNames([
    'transition absolute overflow-y-auto flex justify-between bottom-0 left-0 duration-300 ease-in-out min-h-[20rem] 2xl:min-h-[30rem] w-full translate-y-full',
    show ? 'opacity-1 z-20 delay-300' : 'opacity-0 z-[-999] pointer-events-none',
    'bg-[#F3EDE9] py-40 px-48 xl:p-60 2xl:p-80',
  ]);

  const linksClassNames = useClassNames([
    'grid gap-x-64 gap-y-28 grid-cols-12',
    tileContent ? 'pr-116' : '',
  ]);

  const getColSpan = (link: any) => {
    if (link?.title === "Product Range") {
      if (link?.mega_menu?.length === 3) return "col-span-6";
      if (link?.mega_menu?.length === 2) return "col-span-10 xl:col-span-8";
    }
    return COLUMN_CONFIG[link?.title] || "col-span-4";
  };

  const getGridLayout = (link: any) => {
    if (link?.title === "Product Range") {
      if (link?.mega_menu?.length === 3)
        return "grid-cols-3 gap-x-24 gap-y-6 2xl:gap-x-6 2xl:gap-y-30";

      if (link?.mega_menu?.length === 2)
        return "grid-cols-3 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30";
    }

    return GRID_CONFIG[link?.title] || "grid-cols-3 gap-x-24 gap-y-16";
  };


  const PRODUCT_RANGE_MAP: Record<string, keyof typeof IMAGE_STYLE_CONFIG> = {
    "2": "large",
    default: "small",
  };

  const TITLE_IMAGE_MAP: Record<string, keyof typeof IMAGE_STYLE_CONFIG> = {
    "Planning tools": "large",
    "Kitchen & Laundry": "large",

    "About Us": "small",
    "Support": "small",
    "Our Other Brands": "small",
  };

  const getImageClass = (link: any) => {
    const title = link?.title;
    const productRangeKey = String(link?.mega_menu?.length ?? "default");
    const productRangeResult =
      title === "Product Range"
        ? IMAGE_STYLE_CONFIG[PRODUCT_RANGE_MAP[productRangeKey] ?? "small"]
        : null;
    const titleMappedResult = IMAGE_STYLE_CONFIG[TITLE_IMAGE_MAP[title] ?? "default"];
    return productRangeResult || titleMappedResult;
  };


  return (
    <div className={wrapperClassNames}>
      <div className={linksClassNames}>
        {/*
          `links` here are the CMS `submenu_links` blocks — they carry `title`, `mega_menu`, `ctas` and a
          ContentStack `_metadata.uid`, but NO `label`, so keying on a label yielded `undefined` for every
          column (React then warns about missing keys). The uid is unique across the whole entry; `title`
          is only unique within one dropdown ("Product Range" repeats across them), hence this order.
        */}
        {links?.map((link, linkIdx) => (
          <div key={link?._metadata?.uid ?? link?.title ?? linkIdx} className={getColSpan(link)}>
            <div className="w-min pb-16 2xl:pb-26">
              <Typography className="whitespace-nowrap font-semibold text-[#2B2C39] md:text-16 2xl:text-20">
                {link?.title}
              </Typography>
            </div>

            {/* ============================= CONTENT GRID ============================= */}
            <div className={`grid ${getGridLayout(link)} place-items-stretch`}>
              {link?.mega_menu?.map((field: any, idx: number) => {
                const markdownClass = `${getImageClass(link)} flex ${link?.title === '' ? 'flex-col' : 'flex-col-reverse'
                  } [&>div>img]:mb-8 [&>div>img]:w-full [&>div>img]:object-cover [&>figure>div>a>img]:mb-8 [&>figure>div>a>img]:object-cover [&>img]:mb-8 [&>img]:object-cover`;

                if (!field?.mega_menu_url?.href) {
                  return (
                    <Markdown
                      key={field?._metadata?.uid ?? idx}
                      markdown={field?.mega_menu_content}
                      className={markdownClass}
                    />
                  );
                }

                return (
                  <Link
                    key={field?._metadata?.uid ?? idx}
                    link={field?.mega_menu_url?.href}
                    target={field?.mega_menu_url?.href?.includes('https://') ? '_blank' : ''}
                  >
                    <Markdown markdown={field?.mega_menu_content} className={markdownClass} />
                  </Link>
                );
              })}
            </div>

            {/* ============================= CTA BUTTONS ============================= */}
            {link?.ctas?.map((cta: any, ctaIdx: number) => (
              <Link
                key={cta?._metadata?.uid ?? cta?.button?.title ?? ctaIdx}
                link={cta?.button?.href}
                target={cta?.button?.href?.includes('https://') ? '_blank' : ''}
              >
                <Button
                  icon={<PlusIcon className="w-16 text-[#333]" />}
                  style={{
                    backgroundColor: cta?.button_background_color?.hex,
                    border: `1px solid ${cta?.button_border_color?.hex}`,
                    color: cta?.button_text_color?.hex,
                  }}
                  className="mt-26 rounded-none px-12 py-12 shadow-sm hover:!bg-[#F4F4F4]"
                >
                  <Typography as="span" className="text-14 font-medium text-primary-black">
                    {cta?.button?.title}
                  </Typography>
                </Button>
              </Link>
            ))}
          </div>
        ))}
      </div>

      {tileContent && <HeaderDropdownTile tileContent={tileContent} />}
    </div>
  );
};

export default MenuDropdown;
